import { NavLink } from 'react-router-dom';
import { usePermissionsStore } from '@/stores/permissions';

interface NavItemDef {
  to: string;
  label: string;
  view: string;
  icon: React.ReactNode;
  /** When true, only shown to onderaannemers (isOa) — hidden from employee installers. */
  requiresOa?: boolean;
}

const NAV_ITEMS: NavItemDef[] = [
  {
    to: '/installer',
    label: 'Planning',
    view: 'installer',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    to: '/installer/werk',
    label: 'Mijn werk',
    // Gated to onderaannemers only (isOa from /permissions) — employee installers never see it.
    view: 'installer',
    requiresOa: true,
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 14l2 2 4-4M5 7h14l-1 13a2 2 0 01-2 2H8a2 2 0 01-2-2L5 7zM9 7V5a3 3 0 016 0v2" />
      </svg>
    ),
  },
];

/**
 * Items the current installer may see — permission (`canView`) + OA (`isOa`) gate.
 * Shared by the mobile bottom nav and the desktop top nav so both stay in sync.
 */
export function useVisibleInstallerNavItems(): NavItemDef[] {
  const canView = usePermissionsStore((s) => s.canView);
  const isOa = usePermissionsStore((s) => s.permissions?.isOa ?? false);
  return NAV_ITEMS.filter((item) => canView(item.view) && (!item.requiresOa || isOa));
}

export function InstallerBottomNav() {
  const visibleItems = useVisibleInstallerNavItems();

  return (
    <nav className="fixed bottom-0 w-full min-h-[var(--nav-bottom-h)] bg-white border-t border-gray-200 lg:hidden z-50">
      <div className="flex justify-around pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 min-h-[48px] justify-center px-4 ${isActive ? 'text-td-blue' : 'text-gray-400'}`
            }
          >
            {item.icon}
            <span className="text-[10px] font-medium">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
